/**
 * Admin Users Index Page
 *
 * Lists all users with filtering, pagination, and actions.
 *
 * @fileoverview Admin users index page component
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Button,
  Icon,
  VStack,
  useToast,
  useDisclosure,
  Flex,
} from '@chakra-ui/react';
import { MdPersonAdd } from 'react-icons/md';
import {
  AdminUserTable,
  AdminUserStats,
  AdminUserFilters,
  AdminEditUserModal,
  AdminDeactivateUserModal,
  AdminDeleteUserModal,
  AdminManageMembershipsModal,
} from '../../../components/admin';
import {
  useAdminUsers,
  useAdminUserStats,
  useUserActions,
} from '../../../hooks/useAdminUsers';
import { useAdminTenants } from '../../../hooks/useAdminTenants';
import { useUserProfile } from '../../../hooks/useUserProfile';
import type { AdminUser, UpdateAdminUserInput, UpdateMembershipInput } from '../../../types/admin';

/**
 * Admin Users Index Page
 *
 * Displays a list of all users with filtering and management options.
 *
 * @returns React component
 *
 * @example
 * <AdminUsersIndex />
 */
export const AdminUsersIndex: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { profile: userProfile } = useUserProfile();

  // Hooks for data
  const {
    users,
    loading: usersLoading,
    pagination,
    filters,
    setSearch,
    setIsActive,
    setGlobalRole,
    setTenantId,
    setPage,
    resetFilters,
    refetch,
  } = useAdminUsers();

  const { stats, loading: statsLoading, refetch: refetchStats } = useAdminUserStats();
  const { tenants } = useAdminTenants();
  const {
    activateUser,
    deactivateUser,
    deleteUser,
    sendPasswordReset,
    updateUser,
    updateMembership,
    loading: actionLoading,
    error: actionError,
  } = useUserActions();

  // Modal states
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const editModal = useDisclosure();
  const deactivateModal = useDisclosure();
  const deleteModal = useDisclosure();
  const membershipsModal = useDisclosure();

  /**
   * Handle view user
   */
  const handleView = useCallback((user: AdminUser) => {
    navigate(`/admin/users/${user.id}`);
  }, [navigate]);

  /**
   * Handle edit user
   */
  const handleEdit = useCallback((user: AdminUser) => {
    setSelectedUser(user);
    editModal.onOpen();
  }, [editModal]);

  /**
   * Handle save edit
   */
  const handleSaveEdit = useCallback(async (data: UpdateAdminUserInput): Promise<boolean> => {
    if (!selectedUser) return false;

    const result = await updateUser(selectedUser.id, data);
    if (result) {
      toast({
        title: 'User updated',
        description: `Successfully updated ${selectedUser.email}`,
        status: 'success',
        duration: 3000,
      });
      refetch();
      return true;
    }
    return false;
  }, [selectedUser, updateUser, toast, refetch]);

  /**
   * Handle update membership
   */
  const handleUpdateMembership = useCallback(async (
    userId: string,
    tenantId: string,
    data: UpdateMembershipInput
  ): Promise<AdminUser | null> => {
    const result = await updateMembership(userId, tenantId, data);
    if (result) {
      toast({
        title: 'Membership updated',
        description: 'Successfully updated tenant role',
        status: 'success',
        duration: 3000,
      });
      refetch();
    }
    return result;
  }, [updateMembership, toast, refetch]);

  /**
   * Handle activate user
   */
  const handleActivate = useCallback(async (user: AdminUser) => {
    const result = await activateUser(user.id);
    if (result) {
      toast({
        title: 'User activated',
        description: `Successfully activated ${user.email}`,
        status: 'success',
        duration: 3000,
      });
      refetch();
      refetchStats();
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't activate the user. Please try again.",
        status: 'error',
        duration: 3000,
      });
    }
  }, [activateUser, toast, refetch, refetchStats]);

  /**
   * Handle deactivate user
   */
  const handleDeactivate = useCallback((user: AdminUser) => {
    setSelectedUser(user);
    deactivateModal.onOpen();
  }, [deactivateModal]);

  /**
   * Handle confirm deactivate
   */
  const handleConfirmDeactivate = useCallback(async (): Promise<boolean> => {
    if (!selectedUser) return false;

    const result = await deactivateUser(selectedUser.id);
    if (result) {
      toast({
        title: 'User deactivated',
        description: `Successfully deactivated ${selectedUser.email}`,
        status: 'success',
        duration: 3000,
      });
      refetch();
      refetchStats();
      return true;
    }
    toast({
      title: 'Something went wrong',
      description: "We couldn't deactivate the user. Please try again.",
      status: 'error',
      duration: 3000,
    });
    return false;
  }, [selectedUser, deactivateUser, toast, refetch, refetchStats]);

  /**
   * Handle delete user
   */
  const handleDelete = useCallback((user: AdminUser) => {
    setSelectedUser(user);
    deleteModal.onOpen();
  }, [deleteModal]);

  /**
   * Handle confirm delete
   */
  const handleConfirmDelete = useCallback(async (): Promise<boolean> => {
    if (!selectedUser) return false;

    const result = await deleteUser(selectedUser.id);
    if (result) {
      toast({
        title: 'User deleted',
        description: `Successfully deleted ${selectedUser.email}`,
        status: 'success',
        duration: 3000,
      });
      refetch();
      refetchStats();
      return true;
    }
    
    // Check if user was already deleted (404 error)
    const is404 = actionError?.includes('404') || actionError?.includes('not found');
    if (is404) {
      toast({
        title: 'User already deleted',
        description: `The user ${selectedUser.email} no longer exists. Refreshing list...`,
        status: 'info',
        duration: 3000,
      });
      refetch();
      refetchStats();
      return true; // Return true to close modal since user is effectively gone
    }
    
    toast({
      title: 'Something went wrong',
      description: actionError || "We couldn't delete the user. Please try again.",
      status: 'error',
      duration: 5000,
    });
    return false;
  }, [selectedUser, deleteUser, toast, refetch, refetchStats, actionError]);

  /**
   * Handle manage memberships
   */
  const handleManageMemberships = useCallback((user: AdminUser) => {
    setSelectedUser(user);
    membershipsModal.onOpen();
  }, [membershipsModal]);

  /**
   * Handle password reset
   */
  const handlePasswordReset = useCallback(async (user: AdminUser) => {
    const result = await sendPasswordReset(user.id);
    if (result) {
      toast({
        title: 'Password reset sent',
        description: result.message,
        status: 'success',
        duration: 3000,
      });
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't send the password reset email. Please try again.",
        status: 'error',
        duration: 3000,
      });
    }
  }, [sendPasswordReset, toast]);

  return (
    <Box p={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Users</Heading>
        <Button
          colorScheme="blue"
          leftIcon={<Icon as={MdPersonAdd} />}
          onClick={() => navigate('/admin/users/invite')}
        >
          Invite User
        </Button>
      </Flex>

      <VStack spacing={6} align="stretch">
        {/* Stats */}
        <AdminUserStats stats={stats} loading={statsLoading} />

        {/* Filters */}
        <AdminUserFilters
          search={filters.search || ''}
          isActive={filters.isActive}
          globalRole={filters.globalRole}
          tenantId={filters.tenantId}
          tenants={tenants}
          onSearchChange={setSearch}
          onIsActiveChange={setIsActive}
          onGlobalRoleChange={setGlobalRole}
          onTenantIdChange={setTenantId}
          onReset={resetFilters}
        />

        {/* Table */}
        <AdminUserTable
          users={users}
          loading={usersLoading}
          pagination={pagination}
          currentUserId={userProfile?.id}
          onPageChange={setPage}
          onView={handleView}
          onEdit={handleEdit}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onDelete={handleDelete}
          onPasswordReset={handlePasswordReset}
          onManageMemberships={handleManageMemberships}
        />
      </VStack>

      {/* Modals */}
      <AdminEditUserModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        user={selectedUser}
        onSave={handleSaveEdit}
        onUpdateMembership={handleUpdateMembership}
        loading={actionLoading}
      />

      <AdminDeactivateUserModal
        isOpen={deactivateModal.isOpen}
        onClose={deactivateModal.onClose}
        user={selectedUser}
        onConfirm={handleConfirmDeactivate}
        loading={actionLoading}
      />

      <AdminDeleteUserModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        user={selectedUser}
        onConfirm={handleConfirmDelete}
        loading={actionLoading}
        error={actionError}
      />

      {selectedUser && (
        <AdminManageMembershipsModal
          isOpen={membershipsModal.isOpen}
          onClose={membershipsModal.onClose}
          user={selectedUser}
          onUpdated={() => refetch()}
        />
      )}
    </Box>
  );
};

export default AdminUsersIndex;
